import { createApp } from 'vue';
import './assets/styles/main.css';
import App from './App.vue';
import keycloak from './keycloak';
import axios from 'axios';
import router from './router';
import vuetify from './plugins/vuetify.js';
import Vue3EasyDataTable from 'vue3-easy-data-table';
import 'vue3-easy-data-table/dist/style.css';
import store from './store';
import InstantSearch from 'vue-instantsearch/vue3/es';
import { getPractitioner } from '@/api/practicante';

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
let isLoggingOut = false;

export const closeSession = async () => {
    if (isLoggingOut) return;
    isLoggingOut = true;

    console.warn('>>> Cerrando sesión (closeSession)');

    const sessionId = localStorage.getItem('kc_session_state');
    try {
        if (sessionId && navigator.onLine) {
            await axios.put('/api/session/end', { session_id: sessionId });
        }
    } catch (err) {
        console.error('Error cerrando sesión:', err);
    } finally {
        localStorage.clear();
        try {
            await keycloak.logout({ redirectUri: window.location.origin });
        } catch (e) {
            console.error('Error en logout', e);
        }
        window.location.href = '/';
    }
};

let inactivityTimer;
const MAX_INACTIVITY = 30 * 60 * 1000;

const resetInactivityTimer = () => {
    if (isLoggingOut) return;
    clearTimeout(inactivityTimer);
    inactivityTimer = setTimeout(async () => {
        console.warn('Usuario inactivo — cerrando sesión...');
        await closeSession();
    }, MAX_INACTIVITY);
};

['mousemove', 'keydown', 'click', 'scroll', 'touchstart'].forEach((evt) =>
    window.addEventListener(evt, resetInactivityTimer),
);

const safePut = async (url, payload, retries = 2) => {
    if (isLoggingOut) return;
    try {
        return await axios.put(url, payload);
    } catch (err) {
        if (!navigator.onLine) return;
        if (retries > 0) {
            await new Promise((r) => setTimeout(r, 600));
            return safePut(url, payload, retries - 1);
        }
        throw err;
    }
};

axios.interceptors.request.use(async (config) => {
    if (isLoggingOut) {
        console.warn('>>> Request ignorada — logout en progreso');
        return Promise.reject(new Error('Sesión finalizada'));
    }

    try {
        if (!navigator.onLine) {
            config.headers.Authorization = `Bearer ${keycloak.token}`;
            return config;
        }

        const refreshed = await keycloak.updateToken(30);
        if (!refreshed && !keycloak.authenticated) {
            throw new Error('Token expirado');
        }
        config.headers.Authorization = `Bearer ${keycloak.token}`;
    } catch (err) {
        if (!navigator.onLine) {
            config.headers.Authorization = `Bearer ${keycloak.token}`;
            return config;
        }

        console.warn('Token expirado real — cerrando sesión');
        await closeSession();
        return Promise.reject(err);
    }
    return config;
});

const app = createApp(App);

export const authPromise = new Promise((resolve) => {
    keycloak
        .init({ onLoad: 'login-required', checkLoginIframe: false })
        .then(async (auth) => {
            if (!auth) return closeSession();

            const {
                sub: userId,
                preferred_username: username,
                realm_access,
                session_state,
            } = keycloak.tokenParsed;
            const roles = realm_access?.roles || [];

            const prevSession = localStorage.getItem('kc_session_state');
            if (prevSession && prevSession !== session_state)
                localStorage.removeItem('number');

            localStorage.setItem('user', username);
            localStorage.setItem('id-user', userId);
            localStorage.setItem('roles', JSON.stringify(roles));
            localStorage.setItem('kc_session_state', session_state);

            try {
                if (navigator.onLine) {
                    await axios.post('/api/session/start', {
                        practitioner_id: userId,
                        session_id: session_state,
                        office_id: localStorage.getItem('office_id') || null,
                    });

                    getPractitioner(userId)
                        .then((res) => localStorage.setItem('user', res.name))
                        .catch(() => localStorage.setItem('user', username));
                }
            } catch (err) {
                console.error('Error creando la sesión:', err);
            }
            app.config.globalProperties.$keycloak = keycloak;

            const HEARTBEAT_INTERVAL = 2 * 60 * 1000;

            setInterval(async () => {
                if (isLoggingOut) return; // <-- STOP heartbeat
                if (!keycloak.authenticated) return;
                if (!navigator.onLine) return;

                try {
                    await keycloak.updateToken(30);
                    await safePut('/api/session/ping', {
                        session_id: localStorage.getItem('kc_session_state'),
                    });
                    resetInactivityTimer();
                } catch (err) {
                    if (!navigator.onLine) return;

                    console.warn('Token inválido real — cerrando sesión');
                    await closeSession();
                }
            }, HEARTBEAT_INTERVAL);

            keycloak.onTokenExpired = async () => {
                if (isLoggingOut) return;
                try {
                    if (!navigator.onLine) return;

                    const refreshed = await keycloak.updateToken(30);
                    if (!refreshed)
                        throw new Error('No se pudo refrescar token');
                } catch (err) {
                    console.error('Token expirado — cerrando sesión…');
                    await closeSession();
                }
            };

            resetInactivityTimer();

            app.use(vuetify)
                .use(router)
                .use(store)
                .use(InstantSearch)
                .component('Vue3EasyDataTable', Vue3EasyDataTable)
                .mount('#app');

            resolve();
        })
        .catch((err) => {
            console.error('Autenticación fallida', err);
            closeSession();
        });
});
